import { useEffect, useMemo, useState } from 'react';
import {
  XMarkIcon,
  ChevronDownIcon,
  ChevronRightIcon,
} from '@heroicons/react/24/outline';
import { FlagIcon, MapPinIcon } from '@heroicons/react/24/outline';
import { FlagIcon as FlagSolidIcon } from '@heroicons/react/20/solid';
import Avatar from './Avatar';
import TimeOfDayField from './TimeOfDayField';
import LocationFlagMap from '@/routes/Agency/WorkerV2/components/dependabilityV2/LocationFlagMap';
import DependabilityGateIcon from './DependabilityGateIcon';
import { TimesheetReview, TimesheetReviewEdits } from '../api/types';
import { netHours, timesheetEditError, BREAK_OPTIONS } from '../util/netHours';
import { breakLabel } from '../util/breakLabel';
import { oneDecimal, upToTwoDecimals } from '../util/numberFormat';

type Props = {
  isOpen: boolean;
  review: TimesheetReview | null;
  loading?: boolean;
  error?: string | null;
  onClose: () => void;
  onApprove: (edits: TimesheetReviewEdits) => void;
  onReject: () => void;
};

/** `2026-08-13T18:45:00Z` -> the `datetime-local` value an input wants. */
const toLocalInput = (iso?: string | null): string => {
  if (!iso) return '';
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return '';
  const pad = (n: number) => String(n).padStart(2, '0');
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}T${pad(d.getHours())}:${pad(d.getMinutes())}`;
};

/**
 * The moment to CALCULATE and SUBMIT for one side — the server's own string
 * when the admin has not touched the field, a fresh one when they have.
 *
 * GRAVY-10823. The field edits a clock time to the minute (Figma
 * 39621:199579), but a capture carries seconds: 4:38:52 PM, not 4:38 PM.
 * Re-deriving the ISO from the input string therefore TRUNCATED, and it did so
 * on every approval, not just edited ones — `submit` sends both times
 * unconditionally, and `isDirty` only guards Reject.
 *
 * Two consequences, both real in production:
 *
 *   * Approving an untouched timesheet rewrote `approved_checkin_at` from
 *     20:38:52Z to 20:38:00Z and `approved_checkout_at` from 01:00:18Z to
 *     01:00:00Z — up to 59s of pay-relevant time per side, silently, on a
 *     record ops believed they had left alone.
 *   * Net Hours read 4.37 where the queue's "Total hrs" read 4.36 for the same
 *     timesheet, because the server computes from the untruncated capture
 *     (`Dashboard::NeedsApprovalWorkers#total_hours`) and the modal did not.
 *
 * Comparing through `toLocalInput` is what makes "untouched" decidable: the
 * input holds a minute-precision local string, so the round-trip is the only
 * honest way to ask whether it still represents the server's moment. A side
 * the admin DID retype becomes a fresh minute-precision moment, which is what
 * they typed and what they meant.
 */
const effectiveIso = (input: string, serverIso?: string | null): string => {
  if (!input) return '';
  if (serverIso && input === toLocalInput(serverIso)) return serverIso;

  const d = new Date(input);
  // Unparseable text travels on unchanged so the downstream `Date.parse` still
  // fails and `timesheetEditError` names it — swallowing it to '' here would
  // report a half-typed time as a MISSING one.
  return Number.isNaN(d.getTime()) ? input : d.toISOString();
};

/**
 * The per-worker Review modal (GRAVY-10366 §2.4).
 *
 * Opens for flagged AND clean timesheets — approval is per worker, and a clean
 * one still has to be reviewable (AC). The tag only appears when something is
 * actually flagged, so a clean review does not invent an issue.
 */
const NeedsApprovalReviewModal = ({
  isOpen,
  review,
  loading,
  error,
  onClose,
  onApprove,
  onReject,
}: Props) => {
  const [checkinAt, setCheckinAt] = useState('');
  const [checkoutAt, setCheckoutAt] = useState('');
  const [breakMinutes, setBreakMinutes] = useState(0);
  const [mapOpen, setMapOpen] = useState(true);
  // GRAVY-10813. Reject discards edits server-side; this is the one warning
  // between the click and that happening.
  const [confirmingReject, setConfirmingReject] = useState(false);

  // Re-seed whenever a different worker's review arrives: the modal is mounted
  // once and reused per row, so stale local state would show worker A's edits
  // under worker B's name.
  useEffect(() => {
    setCheckinAt(toLocalInput(review?.checkin.at));
    setCheckoutAt(toLocalInput(review?.checkout.at));
    setBreakMinutes(review?.break_minutes ?? 0);
    // Or worker B inherits a half-confirmed Reject from worker A.
    setConfirmingReject(false);
  }, [
    review?.timesheet_id,
    review?.checkin.at,
    review?.checkout.at,
    review?.break_minutes,
  ]);

  // GRAVY-10823. What every calculation and the Approve payload actually use —
  // seconds preserved on a side nobody edited. Derived once here so Net Hours,
  // the range check and the submit body cannot disagree about which moment
  // this row represents.
  const effectiveCheckinAt = useMemo(
    () => effectiveIso(checkinAt, review?.checkin.at),
    [checkinAt, review?.checkin.at],
  );
  const effectiveCheckoutAt = useMemo(
    () => effectiveIso(checkoutAt, review?.checkout.at),
    [checkoutAt, review?.checkout.at],
  );

  // AC: recalculates live, before Approve is clicked.
  const liveNetHours = useMemo(
    () =>
      netHours(
        effectiveCheckinAt || null,
        effectiveCheckoutAt || null,
        breakMinutes,
      ),
    [effectiveCheckinAt, effectiveCheckoutAt, breakMinutes],
  );

  // GRAVY-10812. What the server would refuse, said before Approve is clicked.
  const editError = useMemo(
    () =>
      timesheetEditError(
        effectiveCheckinAt || null,
        effectiveCheckoutAt || null,
        breakMinutes,
      ),
    [effectiveCheckinAt, effectiveCheckoutAt, breakMinutes],
  );

  // Only the TIME fields' own problem — a break longer than the shift is not a
  // reason to redden the check-in box. Re-running the same check with a zero
  // break isolates the range: with no break to be too long, anything left is a
  // parse or ordering fault in the two timestamps.
  const rangeInvalid = useMemo(
    () =>
      timesheetEditError(
        effectiveCheckinAt || null,
        effectiveCheckoutAt || null,
        0,
      ) != null,
    [effectiveCheckinAt, effectiveCheckoutAt],
  );

  // GRAVY-10813. Has the admin actually changed anything? Compared against the
  // SERVER's values re-normalised through `toLocalInput`, not against a
  // remembered copy of the initial state: the inputs hold minute-precision
  // local strings, so a raw ISO comparison reports every timesheet as edited.
  const isDirty = useMemo(() => {
    if (!review) return false;
    return (
      checkinAt !== toLocalInput(review.checkin.at) ||
      checkoutAt !== toLocalInput(review.checkout.at) ||
      breakMinutes !== (review.break_minutes ?? 0)
    );
  }, [review, checkinAt, checkoutAt, breakMinutes]);

  if (!isOpen) return null;

  /**
   * GRAVY-10811. The side whose map to draw — chosen on the FLAG's presence,
   * never on `flagged`.
   *
   * `flagged` is deliberately false once a timesheet has been decided (D6: the
   * tag and the pink row styling drop away, and the flag icon alone carries
   * status), while `TimesheetReviewService` keeps sending the `flag` record
   * itself — its comment says so outright, "the `flag` record still travels so
   * the map and caption stay renderable". Gating the section on `flagged` threw
   * that away and took the whole map with it, so reopening a decided timesheet
   * showed no evidence at all — exactly when ops is auditing the decision. The
   * Worker Profile modal keeps resolved entries' maps, and these two surfaces
   * are required to agree.
   */
  const mapSide = review?.checkout.location_flag
    ? review.checkout
    : review?.checkin.location_flag
      ? review.checkin
      : null;

  const submit = () =>
    onApprove({
      // GRAVY-10823: `effectiveIso`, never a re-derivation from the input —
      // an untouched side must reach the server with the seconds it was
      // captured with.
      approved_checkin_at: effectiveCheckinAt || undefined,
      approved_checkout_at: effectiveCheckoutAt || undefined,
      approved_break_minutes: breakMinutes,
    });

  const sideRow = (
    label: string,
    side: TimesheetReview['checkin'] | undefined,
    value: string,
    onChange: (v: string) => void,
    testId: string,
    // GRAVY-10818: check-out is anchored to check-in so it can show "+1 day"
    // when the shift runs past midnight. Check-in has nothing to be offset
    // from, so it passes none.
    anchor: string | null = null,
  ) => (
    <div
      className={`dashv2-review-row${side?.flagged ? ' is-flagged' : ''}`}
      data-testid={testId}
    >
      <span className="dashv2-review-rowlabel">{label}</span>
      <div className="dashv2-review-rowcontrol">
        <div className="dashv2-review-rowtop">
          {/* GRAVY-10814. The leading icon sits OUTSIDE the field, 12px from
              it — a flag when the side breached, otherwise a map pin. The
              design draws the pin on the clean side too (39621:199674), so the
              row reads as "a located event" rather than leaving a gap where
              the flag would be. Outline at 16px for the flag, matching
              39958:8921 — the solid 20px glyph belongs to the queue's worker
              rows, which is a different frame. */}
          {side?.flagged ? (
            <FlagIcon
              className="dashv2-review-flag"
              data-testid={`${testId}-flag`}
              aria-label="Location flag"
            />
          ) : (
            <MapPinIcon
              className="dashv2-review-pin"
              data-testid={`${testId}-pin`}
              aria-hidden="true"
            />
          )}
          <TimeOfDayField
            value={value}
            onChange={onChange}
            ariaLabel={label}
            testId={testId}
            invalid={rangeInvalid}
            anchor={anchor}
          />
        </div>
        <p
          className={`dashv2-review-distance${side?.flagged ? ' is-flagged' : ''}`}
          data-testid={`${testId}-distance`}
        >
          {side?.distance_miles == null
            ? '—'
            : `${oneDecimal(side.distance_miles)} mi away`}
        </p>
      </div>
    </div>
  );

  return (
    <div
      className="dashv2-review-overlay"
      role="presentation"
      onClick={onClose}
    >
      <div
        className="dashv2-review"
        role="dialog"
        aria-modal="true"
        aria-label="Review"
        data-testid="dashv2-review-modal"
        onClick={(e) => e.stopPropagation()}
      >
        <header className="dashv2-review-head">
          <h2>Review</h2>
          <button type="button" aria-label="Close" onClick={onClose}>
            <XMarkIcon />
          </button>
        </header>

        <div className="dashv2-review-body">
          <p className="dashv2-review-instruction">
            Review or make adjustments to this timesheet
          </p>

          {loading && <p className="dashv2-review-note">Loading…</p>}
          {error && (
            <p
              className="dashv2-review-error"
              data-testid="dashv2-review-error"
            >
              {error}
            </p>
          )}

          {review && (
            <>
              <div className="dashv2-review-card">
                {/* Only when something IS flagged — the modal opens for clean
                    rows too, and a tag there would invent an issue. */}
                {review.tag && (
                  <span
                    className="dashv2-review-tag"
                    data-testid="dashv2-review-tag"
                  >
                    {review.tag}
                  </span>
                )}

                <div className="dashv2-review-identity">
                  <Avatar
                    src={review.worker?.avatar_url}
                    name={review.worker?.name}
                  />
                  <span className="dashv2-review-name">
                    {review.worker?.name || '—'}
                  </span>
                  {/* D4: reuse the settled tier badge rather than block on an
                      undocumented glyph. Wrapped so the AC ("worker's name shows
                      with a small badge icon next to it") is assertable — an
                      icon component with no handle of its own cannot be tested. */}
                  <span data-testid="dashv2-review-badge">
                    <DependabilityGateIcon />
                  </span>
                  <span
                    className="dashv2-review-similar"
                    data-testid="dashv2-review-similar"
                  >
                    <FlagSolidIcon />
                    {`${review.similar.total_count} similar in last ${review.similar.window_days ?? 30}D`}
                  </span>
                </div>

                {sideRow(
                  'Check-in',
                  review.checkin,
                  checkinAt,
                  setCheckinAt,
                  'dashv2-review-checkin',
                )}
                {sideRow(
                  'Check-out',
                  review.checkout,
                  checkoutAt,
                  setCheckoutAt,
                  'dashv2-review-checkout',
                  checkinAt || null,
                )}

                <div className="dashv2-review-row">
                  <span className="dashv2-review-rowlabel">Unpaid Break</span>
                  <select
                    className="dashv2-review-break"
                    value={breakMinutes}
                    onChange={(e) => setBreakMinutes(Number(e.target.value))}
                    data-testid="dashv2-review-break"
                    aria-label="Unpaid Break"
                  >
                    {/* GRAVY-10814: zero reads "No break", not "0 min"
                        (39621:199609). */}
                    {BREAK_OPTIONS.map((m) => (
                      <option key={m} value={m}>
                        {breakLabel(m)}
                      </option>
                    ))}
                  </select>
                </div>

                <div className="dashv2-review-row">
                  <span className="dashv2-review-rowlabel">Net Hours</span>
                  {/* GRAVY-10812. An em dash, not "0", when the range is
                      invalid. `netHours` clamps a negative span to zero — right
                      for arithmetic, wrong as a readout: a check-out set BEFORE
                      check-in then rendered as a confident "0", which reads as
                      "a zero-hour shift" rather than "these times are
                      impossible", and was the only thing on screen until
                      Approve was clicked and refused. */}
                  <span
                    className={`dashv2-review-net${editError ? ' is-invalid' : ''}`}
                    data-testid="dashv2-review-net-hours"
                  >
                    {editError || liveNetHours == null
                      ? '—'
                      : upToTwoDecimals(liveNetHours)}
                  </span>
                </div>
              </div>

              {/* GRAVY-10812. Said BEFORE Approve, in the server's own words. */}
              {editError && (
                <p
                  className="dashv2-review-warning"
                  role="alert"
                  data-testid="dashv2-review-edit-error"
                >
                  {editError}
                </p>
              )}

              {mapSide?.location_flag && (
                <section className="dashv2-review-location">
                  <button
                    type="button"
                    className="dashv2-review-collapse"
                    aria-expanded={mapOpen}
                    onClick={() => setMapOpen((o) => !o)}
                    data-testid="dashv2-review-map-toggle"
                  >
                    Check-in/Check-out location
                    {mapOpen ? <ChevronDownIcon /> : <ChevronRightIcon />}
                  </button>
                  {mapOpen && (
                    <>
                      {/* The SAME component the Worker Profile modal uses —
                          same anchor, stored radius, flagged point (AC R7). */}
                      <LocationFlagMap
                        anchor={mapSide.location_flag.anchor ?? null}
                        flagged={mapSide.location_flag.flagged_point ?? null}
                        geofenceRadiusFeet={mapSide.location_flag.radius_feet}
                        testId="dashv2-review-map"
                      />
                      <p
                        className="dashv2-review-caption"
                        data-testid="dashv2-review-caption"
                      >
                        {`${mapSide.location_flag.distance_label} away from ${mapSide.location_flag.venue_address}`}
                      </p>
                    </>
                  )}
                </section>
              )}
            </>
          )}
        </div>

        {/* GRAVY-10813. Reject does not persist the edited times — it writes
            `rejected_at` and leaves `approved_checkin_at`/`approved_checkout_at`
            nil — so an admin who corrects the hours and then decides the shift
            should be rejected loses that work. The silence is the bug: the
            behaviour itself is defensible (a rejected timesheet has no approved
            hours to record), so this warns and asks rather than changing what
            Reject writes, which is a product call and not one to make here.

            Only when something was actually edited: an unmodified Reject has
            nothing to lose and must stay one click. */}
        {confirmingReject && (
          <div
            className="dashv2-review-confirm"
            role="alertdialog"
            aria-label="Discard edits and reject"
            data-testid="dashv2-review-reject-confirm"
          >
            <p>
              Your edits to this timesheet won&apos;t be saved when you reject
              it. Reject anyway?
            </p>
            <div className="dashv2-review-confirm-actions">
              <button
                type="button"
                className="dashv2-review-confirm-cancel"
                onClick={() => setConfirmingReject(false)}
                data-testid="dashv2-review-reject-cancel"
              >
                Keep editing
              </button>
              <button
                type="button"
                className="dashv2-review-confirm-go"
                onClick={() => {
                  setConfirmingReject(false);
                  onReject();
                }}
                data-testid="dashv2-review-reject-confirm-go"
              >
                Reject anyway
              </button>
            </div>
          </div>
        )}

        <footer className="dashv2-review-foot">
          <button
            type="button"
            className="dashv2-review-reject"
            onClick={() => {
              if (isDirty) {
                setConfirmingReject(true);
                return;
              }
              onReject();
            }}
            disabled={!review}
            data-testid="dashv2-review-reject"
          >
            Reject
          </button>
          {/* Disabled on an edit the server is certain to refuse (GRAVY-10812).
              `editError` mirrors `ApproveTimesheet`'s own conditions, so this
              blocks only what would come back a 422 anyway — the server stays
              authoritative and still gets the last word on anything this
              cannot see from the browser. `title` carries the reason, since a
              disabled button with no explanation is its own dead end. */}
          <button
            type="button"
            className="dashv2-review-approve"
            onClick={submit}
            disabled={!review || !!editError}
            title={editError ?? undefined}
            data-testid="dashv2-review-approve"
          >
            Approve
          </button>
        </footer>
      </div>
    </div>
  );
};

export default NeedsApprovalReviewModal;
